import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Headphones, Ticket } from "lucide-react";
import { useApp } from "../context/AppContext";
import { chatApi, supportApi } from "../lib/api";

function timeShort(iso) {
  try { return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

const CATEGORIES = [
  { key: "Deposit", q: "Please share your deposit amount and the UTR / transaction reference, and tell us what went wrong (e.g. amount not credited)." },
  { key: "Withdrawals", q: "Please share your withdrawal amount and the bank account it went to, and describe the problem." },
  { key: "Game Issue", q: "Which game were you playing and what happened? Please include the round/time and details." },
  { key: "Bet Issue", q: "Please share the bet reference and describe the issue with your bet." },
  { key: "Other", q: "Please describe your issue in detail." },
];

const GREETING = "Welcome to BatBallWorld Support 👋\nWhat do you need help with? Pick a topic below.";
const CLOSED_KEY = "bbw_chat_closed_seen";

let localId = 0;
const mkMsg = (sender, text) => ({ id: `local-${++localId}`, sender, text, at: new Date().toISOString() });

export default function ChatWidget() {
  const { isAuthed } = useApp();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);      // live admin chat (server)
  const [botMsgs, setBotMsgs] = useState([]);         // scripted bot conversation (local)
  const [stage, setStage] = useState("greeting");     // greeting | awaiting_details | done
  const [category, setCategory] = useState(null);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const greeted = useRef(false);

  const scrollBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  // Admin closed the conversation: wipe everything on this side and restart from the menu.
  const handleClosed = (closedAt) => {
    if (!closedAt || localStorage.getItem(CLOSED_KEY) === closedAt) return;
    localStorage.setItem(CLOSED_KEY, closedAt);
    setMessages([]);
    setBotMsgs([mkMsg("bot", "This conversation was closed by Support. Thanks for reaching out!"), mkMsg("bot", GREETING)]);
    setStage("greeting");
    setCategory(null);
    greeted.current = true;
    scrollBottom();
  };

  const load = async () => {
    try {
      const res = await chatApi.list();
      const msgs = Array.isArray(res) ? res : res.messages || [];
      handleClosed(res?.closedAt);
      setMessages(msgs);
      setUnread(0);
      scrollBottom();
    } catch (e) { console.error("Chat load failed", e); }
  };

  const pollUnread = async () => {
    try {
      const { unread: u, closedAt } = await chatApi.unread();
      setUnread(u);
      handleClosed(closedAt);
    } catch (e) { console.error("Chat unread poll failed", e); }
  };

  useEffect(() => {
    if (!isAuthed) return;
    pollUnread();
    const t = setInterval(() => { if (open) load(); else pollUnread(); }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, open]);

  useEffect(() => {
    if (!open) return;
    load();
    if (!greeted.current) {
      greeted.current = true;
      setBotMsgs([mkMsg("bot", GREETING)]);
      setStage("greeting");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!isAuthed) return null;

  const showGreeting = () => {
    setBotMsgs((p) => [...p, mkMsg("bot", GREETING)]);
    setStage("greeting");
    setCategory(null);
    scrollBottom();
  };

  const chooseCategory = (cat) => {
    if (sending) return;
    const c = CATEGORIES.find((x) => x.key === cat);
    setBotMsgs((p) => [...p, mkMsg("user", cat), mkMsg("bot", c.q)]);
    setCategory(cat);
    setStage("awaiting_details");
    scrollBottom();
  };

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");

    if (stage === "awaiting_details" && category) {
      setBotMsgs((p) => [...p, mkMsg("user", t)]);
      setSending(true);
      try {
        const tk = await supportApi.createTicket({ category, details: t });
        setBotMsgs((p) => [...p, mkMsg("bot",
          `✅ Ticket #${tk.ref} raised. Our team will reply here shortly — please keep an eye on this chat.\n\nType another message to reach our team, or say "hi" to raise a new issue.`)]);
        setStage("done");
        setCategory(null);
      } catch {
        setBotMsgs((p) => [...p, mkMsg("bot", "Sorry, we couldn't create your ticket. Please try again.")]);
      } finally {
        setSending(false);
        scrollBottom();
      }
      return;
    }

    if (/^(hi|hii|hello|hey|help|start|menu)\b/i.test(t)) {
      setBotMsgs((p) => [...p, mkMsg("user", t)]);
      showGreeting();
      return;
    }

    setSending(true);
    try {
      const msg = await chatApi.send(t);
      setMessages((prev) => [...prev, msg]);
      scrollBottom();
    } catch (e) { console.error("Chat send failed", e); }
    finally { setSending(false); }
  };

  const all = [...botMsgs, ...messages].sort((a, b) => String(a.at).localeCompare(String(b.at)));

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label="Support chat" data-testid="chat-launcher"
          className="fixed bottom-20 right-4 z-50 h-12 w-12 rounded-full bg-gradient-to-br from-[#f5b400] to-[#c98f00] text-black grid place-items-center shadow-[0_10px_30px_-8px_rgba(245,180,0,0.7)] ring-1 ring-[#ffd966]/40 hover:scale-105 active:scale-95 transition-transform">
          <MessageCircle className="h-5 w-5" strokeWidth={2.4} />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 h-[18px] min-w-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center ring-2 ring-[#0b0b0b]" data-testid="chat-unread-badge">
              {unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div className="fixed bottom-20 right-4 z-50 w-[calc(100vw-2rem)] sm:w-96 h-[500px] max-h-[75vh] rounded-3xl border border-[#f5b400]/25 bg-[#0b0b0b] text-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] flex flex-col overflow-hidden animate-fade-up" data-testid="chat-panel">
          <div className="relative flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#111318]">
            <div className="absolute -top-10 left-6 h-24 w-24 rounded-full bg-[#f5b400]/20 blur-2xl pointer-events-none" />
            <div className="relative flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-gradient-to-br from-[#f5b400] to-[#c98f00] grid place-items-center shadow-[0_6px_16px_-6px_rgba(245,180,0,0.8)]"><Headphones className="h-4 w-4 text-black" /></div>
              <div>
                <div className="font-display font-bold text-sm tracking-tight">BatBallWorld Support</div>
                <div className="text-[11px] text-emerald-400 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)]" /> Online · replies in minutes</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="relative h-8 w-8 rounded-full bg-white/5 hover:bg-white/10 grid place-items-center text-slate-300 transition-colors" data-testid="chat-close"><X className="h-4 w-4" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar bg-[radial-gradient(ellipse_at_top,rgba(245,180,0,0.06),transparent_55%)]">
            {all.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.sender}`}>
                <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug ${
                  m.sender === "user"
                    ? "bg-gradient-to-br from-[#f5b400] to-[#e0a500] text-black rounded-br-md shadow-[0_6px_16px_-8px_rgba(245,180,0,0.8)]"
                    : "bg-[#15171c] ring-1 ring-white/[0.06] text-slate-100 rounded-bl-md"
                }`}>
                  {m.sender === "admin" && (
                    <div className="text-[10px] font-bold text-[#f5b400] mb-0.5 inline-flex items-center gap-1">
                      {m.ticketRef ? <><Ticket className="h-3 w-3" /> Support · {m.ticketRef}</> : "Support"}
                    </div>
                  )}
                  {m.sender === "bot" && <div className="text-[10px] font-bold text-[#f5b400] mb-0.5">Support Bot</div>}
                  <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[10px] mt-1 ${m.sender === "user" ? "text-black/60" : "text-slate-500"}`}>{timeShort(m.at)}</div>
                </div>
              </div>
            ))}

            {stage === "greeting" && (
              <div className="flex flex-wrap gap-2 pt-1" data-testid="chat-categories">
                {CATEGORIES.map((c) => (
                  <button key={c.key} onClick={() => chooseCategory(c.key)} disabled={sending}
                    data-testid={`chat-cat-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
                    className="rounded-full border border-[#f5b400]/40 bg-[#f5b400]/10 text-[#f5b400] px-3 py-1.5 text-xs font-semibold hover:bg-[#f5b400]/20 active:scale-95 transition-all disabled:opacity-40">
                    {c.key === "Other" ? "Other (specify)" : c.key}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/[0.06] bg-[#111318] p-3 flex items-center gap-2">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={stage === "awaiting_details" ? "Type your issue details…" : "Type a message…"}
              className="flex-1 rounded-full bg-black/40 border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none focus:border-[#f5b400]/60 focus:ring-2 focus:ring-[#f5b400]/20 transition-colors"
              data-testid="chat-input"
            />
            <button onClick={send} disabled={sending || !text.trim()} data-testid="chat-send"
              className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-[#f5b400] to-[#c98f00] text-black grid place-items-center shadow-[0_6px_16px_-6px_rgba(245,180,0,0.8)] disabled:opacity-40 disabled:shadow-none hover:brightness-110 active:scale-95 transition-all">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
